import { useState, useCallback } from 'react'
import { usePrintLabel } from '../../hooks/use-print-label'
import { useDymoSdk } from '../../hooks/use-dymo-sdk'
import { useZebraBrowserPrint } from '../../hooks/use-zebra-browser-print'
import { Alert } from './Alert'
import { btnPrimary } from './styles'

interface PrintLabelButtonProps {
  readonly orderId: string
  readonly templateId?: string
  readonly printerId?: string
  readonly label?: string
  readonly onSuccess?: () => void
  readonly onError?: (e: Error) => void
  readonly disabled?: boolean
}

export function PrintLabelButton({
  orderId,
  templateId,
  printerId,
  label = 'Print Label',
  onSuccess,
  onError,
  disabled = false,
}: PrintLabelButtonProps) {
  const { print, loading } = usePrintLabel()
  const dymo = useDymoSdk()
  const zebra = useZebraBrowserPrint()

  const [printError, setPrintError] = useState<string | null>(null)
  const [printed, setPrinted] = useState(false)

  // SDK availability: if a printerId override is supplied, we trust the caller
  // knows the target and don't gate on local SDK detection. Only disable when
  // both SDKs are absent AND no explicit printer override is given.
  const noSdkAvailable = !dymo.available && !zebra.available
  const sdkDisabled = noSdkAvailable && !printerId

  const sdkTooltip = sdkDisabled
    ? 'No compatible printer driver detected — install DYMO Connect or Zebra Browser Print.'
    : undefined

  const isDisabled = disabled || loading || sdkDisabled

  const handleClick = useCallback(async () => {
    setPrintError(null)
    setPrinted(false)
    try {
      await print({ orderId, printerId, templateId })
      setPrinted(true)
      onSuccess?.()
      // Clear the "Printed ✓" indicator after 2 seconds
      setTimeout(() => setPrinted(false), 2000)
    } catch (err) {
      const e = err instanceof Error ? err : new Error(String(err))
      setPrintError(e.message)
      onError?.(e)
    }
  }, [print, orderId, printerId, templateId, onSuccess, onError])

  return (
    <div style={{ display: 'inline-flex', flexDirection: 'column', gap: '0.25rem' }}>
      <div style={{ display: 'inline-flex', alignItems: 'center', gap: '0.5rem' }}>
        <button
          type="button"
          onClick={handleClick}
          disabled={isDisabled}
          title={sdkTooltip}
          style={{
            ...btnPrimary,
            opacity: isDisabled ? 0.6 : 1,
            cursor: isDisabled ? 'not-allowed' : 'pointer',
            display: 'inline-flex',
            alignItems: 'center',
            gap: '0.375rem',
          }}
        >
          {loading && (
            <span
              aria-label="Printing..."
              style={{
                display: 'inline-block',
                width: '0.875rem',
                height: '0.875rem',
                border: '2px solid rgba(255,255,255,0.4)',
                borderTopColor: '#fff',
                borderRadius: '50%',
                animation: 'spin 0.7s linear infinite',
              }}
            />
          )}
          {loading ? 'Printing...' : label}
        </button>
        {printed && !loading && (
          <span style={{ color: '#87CB16', fontWeight: 600, fontSize: '0.9375rem' }}>Printed ✓</span>
        )}
      </div>
      {printError && <Alert type="error" message={printError} onDismiss={() => setPrintError(null)} />}
    </div>
  )
}
